import { useQuery } from "@apollo/client";
import { Add, Delete, Edit } from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import { ReactElement, ReactNode, useCallback, useState } from "react";

import {
  CreateInboxMutation,
  CreateInboxMutationVariables,
  DeleteEscalationInboxMutation,
  DeleteEscalationInboxMutationVariables,
  EditInboxMutation,
  EditInboxMutationVariables,
  ListSubaccountEscalationAndGeneralInboxesQuery,
  ListSubaccountEscalationAndGeneralInboxesQueryVariables,
  NonCampaignInboxType,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import Table from "~frontend/components/Table";
import {
  DELETE_ESCALATION_INBOX,
  EDIT_INBOX,
  CREATE_INBOX,
  LIST_SUBACCOUNT_ESCALATION_AND_GENERAL_INBOXES,
} from "~frontend/graphql/inboxes";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import useMutation from "~frontend/util/useMutation";

import { AddInboxDialog } from "./AddInboxDialog";
import { EditInboxDialog } from "./EditInboxDialog";
import {
  AddOrEditInboxFormValues,
  EscalationInbox,
  GeneralInbox,
} from "./types";

export function InboxManagement(): ReactElement {
  const { subaccount, subaccountLink } = useSubaccount()!;
  const generalInboxesEnabled = useFeatureFlag("generalInboxes");

  useBreadcrumbs([
    { label: "Inboxes", path: subaccountLink("/inbox-management") },
  ]);

  const listInboxes = useQuery<
    ListSubaccountEscalationAndGeneralInboxesQuery,
    ListSubaccountEscalationAndGeneralInboxesQueryVariables
  >(LIST_SUBACCOUNT_ESCALATION_AND_GENERAL_INBOXES, {
    variables: { subaccountId: subaccount.id },
  });

  const [createInbox] = useMutation<
    CreateInboxMutation,
    CreateInboxMutationVariables
  >(CREATE_INBOX, {
    refetchQueries: [LIST_SUBACCOUNT_ESCALATION_AND_GENERAL_INBOXES],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Error creating inbox",
  });

  const [addGeneralInboxDialogOpen, setAddGeneralInboxDialogOpen] =
    useState(false);

  const [addEscalationInboxDialogOpen, setAddEscalationInboxDialogOpen] =
    useState(false);

  const handleAddInbox = useCallback(
    async (type: NonCampaignInboxType, values: AddOrEditInboxFormValues) => {
      const { errors } = await createInbox({
        variables: {
          input: {
            subaccountId: subaccount.id,
            type,
            name: values.name,
            userIds: values.members.map(member => member.id),
            texterAssignmentBatchSize:
              values.texterAssignmentBatchSizeOverrideSubaccount
                ? values.texterAssignmentBatchSize
                : null,
            texterAssignmentExpirationMinutes:
              values.texterAssignmentExpirationMinutesOverrideSubaccount
                ? values.texterAssignmentExpirationMinutes
                : null,
          },
        },
      });

      if (errors) {
        throw errors;
      } else {
        setAddGeneralInboxDialogOpen(false);
        setAddEscalationInboxDialogOpen(false);
      }
    },
    [
      createInbox,
      subaccount,
      setAddGeneralInboxDialogOpen,
      setAddEscalationInboxDialogOpen,
    ]
  );

  const [editingGeneralInbox, setEditingGeneralInbox] =
    useState<GeneralInbox | null>(null);

  const [editingEscalationInbox, setEditingEscalationInbox] =
    useState<EscalationInbox | null>(null);

  const [editInbox] = useMutation<
    EditInboxMutation,
    EditInboxMutationVariables
  >(EDIT_INBOX, {
    refetchQueries: [LIST_SUBACCOUNT_ESCALATION_AND_GENERAL_INBOXES],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Error editing escalation",
  });

  const handleEditInbox = useCallback(
    async (id: string, values: AddOrEditInboxFormValues) => {
      const { errors } = await editInbox({
        variables: {
          input: {
            inboxId: id,
            name: values.name,
            userIds: values.members.map(member => member.id),
            texterAssignmentBatchSize:
              values.texterAssignmentBatchSizeOverrideSubaccount
                ? values.texterAssignmentBatchSize
                : null,
            texterAssignmentExpirationMinutes:
              values.texterAssignmentExpirationMinutesOverrideSubaccount
                ? values.texterAssignmentExpirationMinutes
                : null,
          },
        },
      });

      if (errors) {
        throw errors;
      } else {
        setEditingEscalationInbox(null);
        setEditingGeneralInbox(null);
      }
    },
    [editInbox, setEditingEscalationInbox, setEditingGeneralInbox]
  );

  const [deleteEscalationInbox] = useMutation<
    DeleteEscalationInboxMutation,
    DeleteEscalationInboxMutationVariables
  >(DELETE_ESCALATION_INBOX, {
    refetchQueries: [LIST_SUBACCOUNT_ESCALATION_AND_GENERAL_INBOXES],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Error deleting escalation inbox",
  });

  const handleDeleteEscalationInbox = useCallback(
    async (inboxId: string) => {
      const { errors } = await deleteEscalationInbox({
        variables: { input: { inboxId } },
      });

      if (errors) {
        throw errors;
      }
    },
    [deleteEscalationInbox]
  );

  return (
    <PageContainer>
      {generalInboxesEnabled && (
        <InboxManagementPageSection
          title="Inboxes"
          description="You can create inboxes and assign members to them in order to direct replies to specific groups and manage your team’s workload. At the start of a campaign you can designate an inbox where replies should route to or you can move specific replies into an inbox from the threads page. Inbox members can access their inboxes and claim replies from the texter page."
          addButtonText="New Inbox"
          onClickAddButton={() => setAddGeneralInboxDialogOpen(true)}
        >
          <AsyncWrapper
            data={listInboxes.data}
            loading={listInboxes.loading}
            errors={listInboxes.error}
          >
            {data => (
              <Table
                noFooter
                emptyState="No inboxes have been created"
                columns={[
                  {
                    key: "name",
                    title: "Inbox",
                    renderValue: inbox => inbox.name,
                  },
                  {
                    key: "members",
                    title: "Inbox Members",
                    align: "left",
                    renderValue: inbox =>
                      inbox.userInboxes
                        .map(userInbox => userInbox.user.fullName)
                        .join(", "),
                  },
                ]}
                rows={data.listSubaccountGeneralInboxes}
                rowActions={[
                  {
                    id: "edit",
                    icon: <Edit />,
                    tooltipLabel: "Edit",
                    onClick: row => setEditingGeneralInbox(row),
                  },
                ]}
              />
            )}
          </AsyncWrapper>
        </InboxManagementPageSection>
      )}
      <InboxManagementPageSection
        title="Escalation Inboxes"
        description="Texters may choose to “escalate” a conversation that they don't feel qualified to continue, in order to remove it from their queue and into an Escalation Inbox where only designated people can access it. You can create different types of escalations for texters to categorize their issue (common examples are language barriers or voter protection issues.)"
        addButtonText="New Escalation Inbox"
        onClickAddButton={() => setAddEscalationInboxDialogOpen(true)}
      >
        <AsyncWrapper
          data={listInboxes.data}
          loading={listInboxes.loading}
          errors={listInboxes.error}
        >
          {data => (
            <Table
              noFooter
              emptyState="No escalation inboxes have been created"
              columns={[
                {
                  key: "name",
                  title: "Escalation Inbox",
                  renderValue: inbox => inbox.name,
                },
                {
                  key: "members",
                  title: "Inbox Members",
                  align: "left",
                  renderValue: inbox =>
                    inbox.userInboxes
                      .map(userInbox => userInbox.user.fullName)
                      .join(", "),
                },
              ]}
              rows={data.listSubaccountEscalationInboxes}
              rowActions={[
                {
                  id: "edit",
                  icon: <Edit />,
                  tooltipLabel: "Edit",
                  onClick: row => setEditingEscalationInbox(row),
                },
                {
                  id: "delete",
                  icon: <Delete />,
                  tooltipLabel: "Delete",
                  onClick: row => handleDeleteEscalationInbox(row.id),
                  confirm: row => ({
                    title: `Delete ${row.name}?`,
                    description: `Are you sure you want to delete ${row.name}? This will remove it from the "Escalation Reason" options available to texters, and any threads in this escalation inbox will be de-escalated and moved back to their previous campaigns.`,
                  }),
                },
              ]}
            />
          )}
        </AsyncWrapper>
      </InboxManagementPageSection>
      <AddInboxDialog
        open={addGeneralInboxDialogOpen}
        inboxType={NonCampaignInboxType.General}
        onClose={() => setAddGeneralInboxDialogOpen(false)}
        onSubmit={values =>
          handleAddInbox(NonCampaignInboxType.General, values)
        }
      />
      {editingGeneralInbox && (
        <EditInboxDialog
          open
          onClose={() => setEditingGeneralInbox(null)}
          onSubmit={values => handleEditInbox(editingGeneralInbox.id, values)}
          inbox={editingGeneralInbox}
          inboxType={NonCampaignInboxType.General}
        />
      )}
      <AddInboxDialog
        open={addEscalationInboxDialogOpen}
        inboxType={NonCampaignInboxType.Escalation}
        onClose={() => setAddEscalationInboxDialogOpen(false)}
        onSubmit={values =>
          handleAddInbox(NonCampaignInboxType.Escalation, values)
        }
      />
      {editingEscalationInbox && (
        <EditInboxDialog
          open
          onClose={() => setEditingEscalationInbox(null)}
          onSubmit={values =>
            handleEditInbox(editingEscalationInbox.id, values)
          }
          inbox={editingEscalationInbox}
          inboxType={NonCampaignInboxType.Escalation}
        />
      )}
    </PageContainer>
  );
}

function InboxManagementPageSection({
  title,
  description,
  addButtonText,
  onClickAddButton,
  children,
}: {
  title: ReactNode;
  description: ReactNode;
  addButtonText: ReactNode;
  onClickAddButton: () => void | Promise<void>;
  children: ReactNode;
}) {
  return (
    <>
      <FlexBox
        justifyContent="space-between"
        alignItems="flex-end"
        gap="24px"
        sx={{ marginBottom: "24px" }}
      >
        <Box>
          <PageHeader isSlim>{title}</PageHeader>
          <Typography variant="h2">{description}</Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={onClickAddButton}
          sx={{ width: "fit-content", minWidth: "fit-content" }}
        >
          {addButtonText}
        </Button>
      </FlexBox>
      <Box sx={{ backgroundColor: palette.white, p: 5, marginBottom: "40px" }}>
        {children}
      </Box>
    </>
  );
}
